import React from "react";
import { QESButton, QESTextInput } from "@app/shared/qes-standards";
import { ResourceTagModel } from "./import/types";
import QESOptionsList , { OptionsListOption } from "@app/shared/qes-standards/forms/OptionsList";
import { useMemo } from "react";
import { ManageResourceTagDisplayModel } from "./types";

interface TagResourceProps {
  resourceTags: ManageResourceTagDisplayModel[]
  tags?: ResourceTagModel[];
  disabled?: boolean;
  handleTaggingChange: (updatedTags: ResourceTagModel[]) => void;
}

const tagGroupNameError = "Group Name cannot be empty if a tag name is selected or created.";
const tagNameError = "Tag Name cannot be empty if a group name is selected or created.";

const isEmpty = (value?: string) => !value || value.trim().length === 0;

const taggingRequired = (tag: ResourceTagModel) => !isEmpty(tag.tagGroupName) || !isEmpty(tag.tagName);
const tagGroupNameOk = (tag: ResourceTagModel) => isEmpty(tag.tagName) || !isEmpty(tag.tagGroupName);
const tagNameOk = (tag: ResourceTagModel) => isEmpty(tag.tagGroupName) || !isEmpty(tag.tagName);

const TagResource: React.FC<React.PropsWithChildren<TagResourceProps>> = (props: TagResourceProps) => {
  const {
    disabled,
    tags,
    resourceTags
  } = props;

  const addTag = () => {
    const newTagResource: ResourceTagModel[] = [...tags, { tagGroupName: "", tagName: "" } as ResourceTagModel];
    props.handleTaggingChange(newTagResource);
  };

  const removeTag = (indexToRemove: number) => {
    const newTagResource: ResourceTagModel[] = tags.filter((_, index) => index !== indexToRemove);
    props.handleTaggingChange(newTagResource);
  };

  const onTagNameChange = (indexChanged: number, newTagNameValue: string) => {
    const updatedTagResources = tags.map((tag, index) =>
      index === indexChanged ? { ...tag, tagName: newTagNameValue } : tag
    );
    
    props.handleTaggingChange(updatedTagResources);
  };

  const onGroupNameChange = (indexChanged: number, newGroupNameValue: string) => {
    const updatedTagResources = tags.map((tag, index) =>
      index === indexChanged ? { ...tag, tagGroupName: newGroupNameValue } : tag
    );

    props.handleTaggingChange(updatedTagResources);
  };

  const tagGroupResources = useMemo(() => {
    const tagGroupNames = [...new Set(resourceTags.map(r => r.tagGroupName))];
    return tagGroupNames.map(groupItem => ({ key: groupItem, value: groupItem } as OptionsListOption));
  }, [resourceTags])

  const tagNameResources = useMemo(() => {
    const tagNames = [...new Set(resourceTags.map(r => r.tagName))];
    return tagNames.map(tagItem => ({ key: tagItem, value: tagItem } as OptionsListOption));
  }, [resourceTags])

  return (
    <>
      {tags.map((tag, index) => (
        <React.Fragment key={`tag-resource-group-${index}`}>
          <div className="col-3 offset-2">
            <QESTextInput
              label="Group Name"
              placeholder="Select or create a group"
              maxLength={255}
              value={tag.tagGroupName}
              onChange={(e) => onGroupNameChange(index, e.target.value)}
              testId={`tag-resource__group-name-input--row-${index}`}
              descriptiveText={!tagGroupNameOk(tag) && tagGroupNameError}
              required={taggingRequired(tag)}
              isValid={tagGroupNameOk(tag)}
              wasVisited={true}
              disabled={disabled}
              optionsList={<QESOptionsList options={tagGroupResources} textIfNoMatch="No groups found."/>}
            />
          </div>
          <div className="col-3">
            <QESTextInput
              label="Tag Name"
              placeholder="Select or create a tag"
              maxLength={255}
              value={tag.tagName}
              onChange={(e) => onTagNameChange(index, e.target.value)}
              testId={`tag-resource__tag-name-input--row-${index}`}
              descriptiveText={!tagNameOk(tag) && tagNameError}
              required={taggingRequired(tag)}
              isValid={tagNameOk(tag)}
              wasVisited={true}
              disabled={disabled}
              optionsList={<QESOptionsList options={tagNameResources} textIfNoMatch="No tags found."/>}
            />
          </div>

          {tags.length > 1 && (
            <div className="col-2 align-content-center">
              <QESButton
                qesTestId={`tag-resource__remove-tag-button--row-${index}`}
                qesType="secondary-danger"
                onClick={() => removeTag(index)}
                disabled={disabled}>
                  Remove Tag
              </QESButton>
            </div>
          )}
        </React.Fragment>
      ))}

      <div className="row col-10 offset-2">
        <QESButton
          qesTestId="tag-resource__add-additional-tag-button"
          qesType="secondary"
          onClick={addTag}
          disabled={disabled}>
            <i className="fa fa-plus pr5" /> Add Additional Tag
        </QESButton>
      </div>
    </>
  );
};

export default TagResource;
